/**
 * Compliance Summary Widget
 * 
 * A compact dashboard widget showing real-time compliance status
 * for HOPE, F2F, Med Rec, and iQIES submissions.
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Shield, ClipboardList, Stethoscope, Pill, FileText,
  ChevronRight, AlertTriangle, CheckCircle, Clock,
  TrendingUp, AlertCircle, ExternalLink
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ComplianceSummaryWidget({ isDark = true, compact = false }) {
  const navigate = useNavigate();
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchComplianceData = async () => {
      try {
        setLoading(true);
        
        // Fetch all compliance data in parallel
        const [hopeRes, f2fRes, medRecRes, iqiesRes] = await Promise.all([
          fetch(`${API_URL}/api/compliance-hub/hope/compliance-status`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/compliance-hub/f2f/pending`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/compliance-hub/medication-reconciliation/status`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/compliance-hub/iqies/export-status`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        const [hope, f2f, medRec, iqies] = await Promise.all([
          hopeRes.ok ? hopeRes.json() : null,
          f2fRes.ok ? f2fRes.json() : null,
          medRecRes.ok ? medRecRes.json() : null,
          iqiesRes.ok ? iqiesRes.json() : null
        ]);

        // Calculate overall compliance score
        const hopeComplete = hope?.complete_count || 0;
        const hopeTotal = hope?.total_count || 0;
        const f2fComplete = f2f?.complete_count || 0;
        const f2fTotal = f2f?.total_count || 0;
        const medRecComplete = medRec?.complete_count || 0;
        const medRecTotal = medRec?.total_count || 0;

        const totalComplete = hopeComplete + f2fComplete + medRecComplete;
        const totalItems = hopeTotal + f2fTotal + medRecTotal;
        const overallScore = totalItems > 0 ? Math.round((totalComplete / totalItems) * 100) : 100;

        setComplianceData({
          overallScore,
          hope: {
            due: hope?.overdue_count || 0,
            pending: hope?.pending_count || 0,
            total: hopeTotal,
            status: (hope?.overdue_count || 0) > 0 ? "critical" : "good"
          },
          f2f: {
            pending: f2f?.pending_count || 0,
            overdue: f2f?.overdue_count || 0,
            total: f2fTotal,
            status: (f2f?.overdue_count || 0) > 0 ? "critical" : (f2f?.pending_count || 0) > 0 ? "warning" : "good"
          },
          medRec: {
            incomplete: medRec?.incomplete_count || 0,
            total: medRecTotal,
            status: (medRec?.incomplete_count || 0) > 0 ? "warning" : "good"
          },
          iqies: {
            ready: iqies?.ready_for_export || 0,
            deadline: iqies?.next_deadline?.days_remaining || 0,
            status: (iqies?.next_deadline?.days_remaining || 30) < 5 ? "warning" : "good"
          }
        });
      } catch (error) {
        console.error("Compliance widget fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchComplianceData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchComplianceData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const getStatusColor = (status) => {
    switch (status) {
      case "critical": return "text-red-400";
      case "warning": return "text-amber-400";
      case "good": return "text-emerald-400";
      default: return isDark ? "text-slate-400" : "text-gray-500";
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "critical": return isDark ? "bg-red-500/10 border-red-500/30" : "bg-red-50 border-red-200";
      case "warning": return isDark ? "bg-amber-500/10 border-amber-500/30" : "bg-amber-50 border-amber-200";
      case "good": return isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-emerald-200";
      default: return isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"} animate-pulse`}>
        <div className="h-4 w-24 bg-slate-700 rounded mb-3" />
        <div className="h-8 w-full bg-slate-700 rounded" />
      </div>
    );
  }

  if (compact) {
    // Ultra-compact version for tight spaces
    return (
      <div 
        className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.02] ${
          isDark ? "bg-slate-800/50 hover:bg-slate-800" : "bg-gray-50 hover:bg-gray-100"
        }`}
        onClick={() => navigate("/compliance-hub")}
        data-testid="compliance-widget-compact"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className={`text-xs font-medium ${isDark ? "text-slate-300" : "text-gray-700"}`}>
              Compliance
            </span>
          </div>
          <Badge className={`text-xs ${
            complianceData?.overallScore >= 90 ? "bg-emerald-500/20 text-emerald-400" :
            complianceData?.overallScore >= 70 ? "bg-amber-500/20 text-amber-400" :
            "bg-red-500/20 text-red-400"
          }`}>
            {complianceData?.overallScore || 0}%
          </Badge>
        </div>
        <Progress value={complianceData?.overallScore || 0} className="h-1.5" />
        {(complianceData?.hope?.due > 0 || complianceData?.f2f?.overdue > 0) && (
          <div className="flex items-center gap-1 mt-2 text-xs text-red-400">
            <AlertTriangle className="w-3 h-3" />
            <span>Action needed</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="compliance-summary-widget">
      {/* Header with Score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isDark ? "bg-emerald-500/20" : "bg-emerald-100"
          }`}>
            <Shield className="w-4 h-4 text-emerald-500" />
          </div>
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider ${
              isDark ? "text-slate-500" : "text-gray-500"
            }`}>Compliance</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-bold ${
            complianceData?.overallScore >= 90 ? "text-emerald-400" :
            complianceData?.overallScore >= 70 ? "text-amber-400" :
            "text-red-400"
          }`}>
            {complianceData?.overallScore || 0}%
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <Progress value={complianceData?.overallScore || 0} className="h-2" />

      {/* Status Items */}
      <div className="space-y-2">
        {/* HOPE Status */}
        <div 
          className={`p-2 rounded-lg border flex items-center justify-between cursor-pointer transition-colors ${
            getStatusBg(complianceData?.hope?.status)
          }`}
          onClick={() => navigate("/compliance-hub")}
        >
          <div className="flex items-center gap-2">
            <ClipboardList className={`w-4 h-4 ${getStatusColor(complianceData?.hope?.status)}`} />
            <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-700"}`}>HOPE</span>
          </div>
          <div className="flex items-center gap-2">
            {complianceData?.hope?.due > 0 && (
              <Badge className="text-xs bg-red-500/20 text-red-400 border-red-500/30">
                {complianceData.hope.due} overdue
              </Badge>
            )}
            {complianceData?.hope?.due === 0 && (
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            )}
          </div>
        </div>

        {/* F2F Status */}
        <div 
          className={`p-2 rounded-lg border flex items-center justify-between cursor-pointer transition-colors ${
            getStatusBg(complianceData?.f2f?.status)
          }`}
          onClick={() => navigate("/compliance-hub")}
        >
          <div className="flex items-center gap-2">
            <Stethoscope className={`w-4 h-4 ${getStatusColor(complianceData?.f2f?.status)}`} />
            <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-700"}`}>F2F</span>
          </div>
          <div className="flex items-center gap-2">
            {complianceData?.f2f?.overdue > 0 && (
              <Badge className="text-xs bg-red-500/20 text-red-400 border-red-500/30">
                {complianceData.f2f.overdue} overdue
              </Badge>
            )}
            {complianceData?.f2f?.pending > 0 && complianceData?.f2f?.overdue === 0 && (
              <Badge className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
                {complianceData.f2f.pending} pending
              </Badge>
            )}
            {complianceData?.f2f?.pending === 0 && complianceData?.f2f?.overdue === 0 && (
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            )}
          </div>
        </div>

        {/* Med Rec Status */}
        <div 
          className={`p-2 rounded-lg border flex items-center justify-between cursor-pointer transition-colors ${
            getStatusBg(complianceData?.medRec?.status)
          }`}
          onClick={() => navigate("/compliance-hub")}
        >
          <div className="flex items-center gap-2">
            <Pill className={`w-4 h-4 ${getStatusColor(complianceData?.medRec?.status)}`} />
            <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-700"}`}>Med Rec</span>
          </div>
          <div className="flex items-center gap-2">
            {complianceData?.medRec?.incomplete > 0 && (
              <Badge className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
                {complianceData.medRec.incomplete} incomplete
              </Badge>
            )}
            {complianceData?.medRec?.incomplete === 0 && (
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            )}
          </div>
        </div>

        {/* iQIES Status */}
        <div 
          className={`p-2 rounded-lg border flex items-center justify-between cursor-pointer transition-colors ${
            getStatusBg(complianceData?.iqies?.status)
          }`}
          onClick={() => navigate("/compliance-hub")}
        >
          <div className="flex items-center gap-2">
            <FileText className={`w-4 h-4 ${getStatusColor(complianceData?.iqies?.status)}`} />
            <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-700"}`}>iQIES</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs ${
              complianceData?.iqies?.deadline < 5 
                ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                : isDark ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"
            }`}>
              {complianceData?.iqies?.deadline}d to deadline
            </Badge>
            {complianceData?.iqies?.ready > 0 && (
              <Badge className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                {complianceData.iqies.ready} ready
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Quick Action Button */}
      <Button
        variant="outline"
        size="sm"
        className={`w-full ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
        onClick={() => navigate("/compliance-hub")}
        data-testid="open-compliance-hub-btn"
      >
        <Shield className="w-4 h-4 mr-2" />
        Open Compliance Hub
        <ChevronRight className="w-4 h-4 ml-auto" />
      </Button>
    </div>
  );
}

export default ComplianceSummaryWidget;
